import React from 'react'
import {
  PieChart,
  BarChart3,
  TrendingUp,
  Target,
  CheckCircle,
  Circle
} from 'lucide-react'
import { AnalysisCoverageData } from '@/types/portfolio'

interface AnalysisCoverageChartProps {
  coverage: AnalysisCoverageData
  loading?: boolean
}

export function AnalysisCoverageChart({ coverage, loading = false }: AnalysisCoverageChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    )
  }

  const totalProperties = Object.values(coverage.properties_by_type).reduce((sum, count) => sum + count, 0)

  if (totalProperties === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <PieChart className="w-5 h-5 mr-2" />
          Analysis Coverage
        </h3>
        <div className="text-center py-8">
          <Circle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No analysis data available</p>
        </div>
      </div>
    )
  }

  const getCoverageColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100'
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100'
    if (percentage >= 40) return 'text-orange-600 bg-orange-100'
    return 'text-red-600 bg-red-100'
  }

  const formatPercentage = (value: number) => `${Math.round(value)}%`

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <PieChart className="w-5 h-5 mr-2" />
        Analysis Coverage Overview
      </h3>

      {/* Coverage Rates */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center">
            <Target className="w-4 h-4 mr-1" />
            Coverage by Type
          </h4>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Buy & Hold</span>
              <div className="flex items-center">
                <div className={`px-2 py-1 rounded text-xs font-medium ${getCoverageColor(coverage.completion_rates.buy_hold_coverage)}`}>
                  {formatPercentage(coverage.completion_rates.buy_hold_coverage)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Fix & Flip</span>
              <div className="flex items-center">
                <div className={`px-2 py-1 rounded text-xs font-medium ${getCoverageColor(coverage.completion_rates.flip_coverage)}`}>
                  {formatPercentage(coverage.completion_rates.flip_coverage)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">BRRR</span>
              <div className="flex items-center">
                <div className={`px-2 py-1 rounded text-xs font-medium ${getCoverageColor(coverage.completion_rates.brrr_coverage)}`}>
                  {formatPercentage(coverage.completion_rates.brrr_coverage)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-700">Full Coverage</span>
              <div className="flex items-center">
                <div className={`px-2 py-1 rounded text-xs font-medium ${getCoverageColor(coverage.completion_rates.full_coverage)}`}>
                  {formatPercentage(coverage.completion_rates.full_coverage)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center">
            <BarChart3 className="w-4 h-4 mr-1" />
            Analysis Distribution
          </h4>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Single Analysis</span>
              <span className="text-sm font-medium text-gray-900">
                {coverage.analysis_distribution.single_analysis}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Multiple Analyses</span>
              <span className="text-sm font-medium text-gray-900">
                {coverage.analysis_distribution.multiple_analyses}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Comprehensive</span>
              <span className="text-sm font-medium text-gray-900">
                {coverage.analysis_distribution.comprehensive_analysis}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Property Type Breakdown */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Properties by Analysis Type</h4>

        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <div className="text-lg font-bold text-blue-600">
              {coverage.properties_by_type['buy-hold']}
            </div>
            <div className="text-xs text-gray-600">Buy & Hold Only</div>
          </div>

          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-lg font-bold text-green-600">
              {coverage.properties_by_type['flip']}
            </div>
            <div className="text-xs text-gray-600">Flip Only</div>
          </div>

          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="text-lg font-bold text-purple-600">
              {coverage.properties_by_type['brrr']}
            </div>
            <div className="text-xs text-gray-600">BRRR Only</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="text-center p-3 bg-indigo-50 rounded-lg">
            <div className="text-lg font-bold text-indigo-600">
              {coverage.properties_by_type['multiple_types']}
            </div>
            <div className="text-xs text-gray-600">Multiple Types</div>
          </div>

          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-bold text-gray-600">
              {coverage.properties_by_type['no_analysis']}
            </div>
            <div className="text-xs text-gray-600">No Analysis</div>
          </div>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Portfolio Analysis Completion</span>
          <div className="flex items-center">
            {coverage.completion_rates.full_coverage > 75 ? (
              <CheckCircle className="w-4 h-4 text-green-600 mr-1" />
            ) : (
              <TrendingUp className="w-4 h-4 text-orange-600 mr-1" />
            )}
            <span className="font-medium">
              {formatPercentage(coverage.completion_rates.full_coverage)} complete
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}